<template>
  <div class="cashier-desk" :class="{ 'in-pos': $route.path === '/pos' }">
    <!-- 顶部：工具栏 + 会员条 -->
    <top-bar @hang="hangOrder" @take="takeVisible = true" @return="returnVisible = true"
             @shift="shiftVisible = true" @lock="locked = true" @screen="openCustomerScreen" />
    <div class="desk-top">
      <member-bar :member="member" @located="onMemberLocated" @clear="onMemberClear" ref="memberBar" />
    </div>
    <div class="desk-main">
      <!-- 左：商品区 -->
      <div class="desk-left">
        <search-box ref="searchBox" :levelId="member ? member.levelId : null"
                    @pick-goods="onSearchPick" @pick-member="onMemberLocated"
                    @scan-order="verifyOrderQr" />
        <category-chips :categories="categories" :activeId="activeCategoryId"
                        :activeChildId="activeChildId"
                        @select="onCategorySelect" @select-child="onChildCategorySelect" />
        <div class="goods-area">
          <goods-grid :rows="goodsRows" @pick="onGoodsPick" />
          <div class="goods-more">
            <a-button v-if="hasMore" size="small" @click="loadMore">加载更多</a-button>
            <span v-else class="goods-end">共 {{ total }} 个商品</span>
          </div>
        </div>
      </div>
      <!-- 右：购物车常驻 -->
      <div class="desk-right">
        <cart-panel :items="cart" :member="member"
                    @change="onItemChange" @changePrice="onItemPriceChange"
                    @remove="onItemRemove" @clear="onCartClear" @settle="openPay" />
      </div>
    </div>
    <!-- 仓库选择 -->
    <div class="desk-depot">
      仓库：
      <a-select v-model="depotId" style="width: 160px" size="small">
        <a-select-option v-for="d in depots" :key="d.id" :value="d.id">{{ d.name }}</a-select-option>
      </a-select>
    </div>
    <!-- 规格选择 -->
    <spec-select-modal :visible="specVisible" :goods="specGoods" @ok="onSpecPick" @cancel="specVisible = false" />
    <!-- 支付 -->
    <pay-modal ref="payModal" :visible="payVisible" :total="cartTotal" :loading="settling" :pay-qr="payQr"
               @ok="onSettle" @cancel="onPayCancel" @create-qrcode="onCreateQrcode" />
    <!-- 完成 -->
    <receipt-done :visible="doneVisible" :result="settleResult" @next="onNextOrder" />
    <!-- 退货 -->
    <return-modal :visible="returnVisible" @cancel="returnVisible = false" @done="loadGoods(1, true)" />
    <!-- 交班 -->
    <shift-modal :visible="shiftVisible" @cancel="shiftVisible = false" />
    <!-- 取单列表 -->
    <a-modal :visible="takeVisible" title="取单（F3）" :footer="null" width="560px"
             @cancel="takeVisible = false">
      <div v-if="!pendingOrders.length" class="pending-empty">暂无挂单</div>
      <div v-for="order in pendingOrders" :key="order.id" class="pending-card">
        <div class="pending-line">
          <span class="pending-no">{{ order.no }}</span>
          <span class="pending-member">{{ order.member ? (order.member.contacts || order.member.cardNo) : '散客' }}</span>
          <span class="pending-amount">¥{{ order.total.toFixed(2) }}</span>
        </div>
        <div class="pending-line sub">
          <span>{{ order.time }} · {{ order.count }} 件</span>
          <span>
            <a-button type="primary" size="small" @click="takeOrder(order)">取单</a-button>
            <a-button size="small" style="margin-left: 8px" @click="removePending(order)">删除</a-button>
          </span>
        </div>
      </div>
    </a-modal>
    <!-- 锁屏 -->
    <lock-screen :visible="locked" @unlock="locked = false" />
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import TopBar from './TopBar'
  import MemberBar from './MemberBar'
  import SearchBox from './SearchBox'
  import CategoryChips from './CategoryChips'
  import GoodsGrid from './GoodsGrid'
  import CartPanel from './CartPanel'
  import SpecSelectModal from './SpecSelectModal'
  import PayModal from './PayModal'
  import ReceiptDone from './ReceiptDone'
  import ReturnModal from './ReturnModal'
  import ShiftModal from './ShiftModal'
  import LockScreen from './LockScreen'
  import { cashierGoods, cashierSettle, cashierScanParse, cashierScanVerify, cashierMemberPoints,
    cashierPayBar, cashierPayQrcode } from '@/api/plugin/member'

  const DEPOT_CACHE_KEY = 'POS_DEPOT_ID'
  const SCREEN_CHANNEL = 'cashier-screen'
  const SCREEN_WINDOW_NAME = 'cashier-customer-screen'
  const MEMBER_POINTS_DEBOUNCE = 300

  /**
   * 零售收银台（040103）：calc(100vh-头部) 满屏自适应，左商品右购物车常驻，
   * 快捷键 F1锁屏/F2挂单/F3取单/F4退货/F5搜索/F9结算/F12交班
   * 顾客副屏：BroadcastChannel 广播 cart/member/payStart/payQrcode/paySuccess/reset
   */
  export default {
    name: 'CashierDesk',
    components: { TopBar, MemberBar, SearchBox, CategoryChips, GoodsGrid, CartPanel,
      SpecSelectModal, PayModal, ReceiptDone, ReturnModal, ShiftModal, LockScreen },
    data() {
      return {
        member: null,
        categories: [],
        depots: [],
        depotId: null,
        activeCategoryId: null,
        activeChildId: null,
        goodsRows: [],
        total: 0,
        currentPage: 1,
        pageSize: 50,
        cart: [],
        specVisible: false,
        specGoods: null,
        payVisible: false,
        settling: false,
        payQr: null,
        doneVisible: false,
        settleResult: {},
        returnVisible: false,
        shiftVisible: false,
        takeVisible: false,
        locked: false,
        pendingOrders: [],
        pendingSeq: 0,
        screenChannel: null,
        pointsTimer: null
      }
    },
    computed: {
      ...mapGetters(['userInfo']),
      pendingKey() {
        const uid = (this.userInfo && this.userInfo.id) || 0
        const tid = (this.userInfo && this.userInfo.tenantId) || 0
        return `POS_PENDING_${tid}_${uid}`
      },
      cartTotal() {
        return this.cart.reduce((sum, i) => sum + Number(i.unitPrice || 0) * Number(i.operNumber || 0), 0)
      },
      hasMore() {
        return this.currentPage * this.pageSize < this.total
      }
    },
    watch: {
      // 购物车变动 → 副屏清单实时刷新（会员在场时同步刷新本次可得积分）
      cart: {
        deep: true,
        handler() {
          this.broadcastCart()
          this.scheduleMemberPoints()
        }
      },
      // 进入支付环节 → 副屏切支付态；取消支付 → 广播清单快照让副屏回到录入/会员态
      payVisible(val) {
        if (val) {
          this.broadcast('payStart', { amount: this.cartTotal })
        } else {
          this.broadcastCart()
        }
      }
    },
    mounted() {
      this.depotId = this.getCacheDepot()
      this.loadGoods(1, true)
      this.loadPending()
      document.addEventListener('keydown', this.handleKey)
      if (typeof BroadcastChannel !== 'undefined') {
        this.screenChannel = new BroadcastChannel(SCREEN_CHANNEL)
      }
    },
    beforeDestroy() {
      document.removeEventListener('keydown', this.handleKey)
      if (this.screenChannel) {
        this.screenChannel.close()
        this.screenChannel = null
      }
      if (this.pointsTimer) {
        clearTimeout(this.pointsTimer)
      }
    },
    methods: {
      // ===== 商品加载 =====
      loadGoods(page, withMeta) {
        const params = {
          categoryId: this.activeChildId || this.activeCategoryId || undefined,
          search: undefined,
          levelId: this.member ? this.member.levelId : undefined,
          currentPage: page,
          pageSize: this.pageSize
        }
        cashierGoods(params).then(res => {
          if (res && res.code === 200) {
            const data = res.data || {}
            if (withMeta) {
              this.categories = data.categories || []
              this.depots = data.depots || []
              this.initDefaultDepot()
            }
            this.total = data.total || 0
            this.currentPage = page
            const rows = data.rows || []
            this.goodsRows = page === 1 ? rows : this.goodsRows.concat(rows)
          } else {
            this.$message.warning((res && res.message) || '商品加载失败')
          }
        })
      },
      initDefaultDepot() {
        if (!this.depotId && this.depots.length) {
          const def = this.depots.find(d => d.is_default)
          this.depotId = def ? def.id : this.depots[0].id
        }
      },
      getCacheDepot() {
        const cached = localStorage.getItem(DEPOT_CACHE_KEY)
        return cached ? Number(cached) : null
      },
      loadMore() {
        this.loadGoods(this.currentPage + 1, false)
      },
      // ===== 分类（一级 + 二级） =====
      onCategorySelect(categoryId) {
        if (categoryId !== this.activeCategoryId) {
          this.activeChildId = null
        }
        this.activeCategoryId = categoryId
        this.loadGoods(1, true)
      },
      onChildCategorySelect({ parentId, childId }) {
        this.activeCategoryId = parentId
        this.activeChildId = childId
        this.loadGoods(1, true)
      },
      // ===== 商城订单码核销（P0-9）=====
      verifyOrderQr(qrContent) {
        cashierScanParse(qrContent).then(res => {
          if (!res || res.code !== 200) {
            this.$message.warning((res && res.message) || '核销码解析失败')
            return
          }
          const order = res.data || {}
          if (order.orderStatus !== '10' && order.orderStatus !== '20') {
            this.$message.warning('订单已完成或已取消，无需核销')
            return
          }
          const items = order.items || []
          const lines = items.map(i => `${i.materialName} ${i.unit}×${i.operNumber}`).join('；')
          const payText = order.payType === 'online' ? (order.payStatus === '1' ? '已在线支付' : '在线未支付') : '到店支付'
          this.$confirm({
            title: `核销确认 - ${order.orderNo}`,
            content: `会员：${order.memberName || '-'}｜金额：¥${order.totalPrice}｜${payText}；明细：${lines}`,
            okText: '确认核销',
            cancelText: '取消',
            onOk: () => {
              return cashierScanVerify(qrContent).then(r => {
                if (r && r.code === 200) {
                  this.$message.success('核销成功，已出库')
                } else {
                  this.$message.warning((r && r.message) || '核销失败')
                }
              })
            }
          })
        })
      },
      // ===== 会员 =====
      onMemberLocated(member) {
        this.member = member
        this.loadGoods(1, true)
        this.refreshCartPrices()
        this.broadcastMember()
      },
      onMemberClear() {
        this.member = null
        this.loadGoods(1, true)
        this.refreshCartPrices()
        this.broadcast('member', null)
      },
      /** 会员切换后未改价行价格跟随刷新（按 extendId 匹配新价格） */
      refreshCartPrices() {
        const priceMap = {}
        this.goodsRows.forEach(goods => {
          ;(goods.units || []).forEach(unit => {
            priceMap[unit.extendId] = unit
          })
        })
        this.cart.forEach(item => {
          if (!item.priceChanged && priceMap[item.extendId]) {
            const unit = priceMap[item.extendId]
            item.unitPrice = Number(unit.price)
            item.basePrice = Number(unit.retailPrice)
          }
        })
      },
      // ===== 入车 =====
      onGoodsPick(goods) {
        const units = goods.units || []
        if (units.length === 1) {
          this.addToCart(goods, units[0])
        } else if (units.length > 1) {
          this.specGoods = goods
          this.specVisible = true
        }
      },
      onSearchPick({ goods, unit }) {
        this.addToCart(goods, unit)
      },
      onSpecPick({ goods, unit }) {
        this.addToCart(goods, unit)
      },
      addToCart(goods, unit) {
        const key = unit.extendId
        const exist = this.cart.find(i => i.key === key)
        if (exist) {
          exist.operNumber = Number(exist.operNumber) + 1
        } else {
          this.cart.push({
            key,
            materialId: goods.materialId,
            extendId: unit.extendId,
            name: goods.name,
            unit: unit.unit,
            barCode: unit.barCode,
            unitPrice: Number(unit.price),
            basePrice: Number(unit.retailPrice),
            operNumber: 1,
            priceChanged: false,
            remark: null
          })
        }
      },
      // ===== 购物车操作 =====
      onItemChange({ index, operNumber }) {
        this.$set(this.cart, index, { ...this.cart[index], operNumber })
      },
      onItemPriceChange({ index, unitPrice }) {
        this.$set(this.cart, index, { ...this.cart[index], unitPrice, priceChanged: true })
      },
      onItemRemove(index) {
        this.cart.splice(index, 1)
      },
      onCartClear() {
        this.cart = []
      },
      // ===== 挂单 / 取单（F2/F3） =====
      loadPending() {
        try {
          const raw = localStorage.getItem(this.pendingKey)
          const list = raw ? JSON.parse(raw) : []
          this.pendingOrders = Array.isArray(list) ? list : []
          this.pendingSeq = this.pendingOrders.reduce((max, o) => Math.max(max, o.id || 0), 0)
        } catch (e) {
          this.pendingOrders = []
        }
      },
      savePending() {
        localStorage.setItem(this.pendingKey, JSON.stringify(this.pendingOrders))
      },
      hangOrder() {
        if (!this.cart.length) {
          this.$message.warning('购物车为空，无需挂单')
          return
        }
        const d = new Date()
        const p = n => String(n).padStart(2, '0')
        this.pendingOrders.unshift({
          id: ++this.pendingSeq,
          no: `挂单${this.pendingSeq}`,
          time: `${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`,
          member: this.member,
          depotId: this.depotId,
          items: JSON.parse(JSON.stringify(this.cart)),
          count: this.cart.reduce((sum, i) => sum + Number(i.operNumber || 0), 0),
          total: this.cartTotal
        })
        this.savePending()
        this.cart = []
        this.member = null
        // 副屏显式同步：挂单即无交易，立即回广告页（不依赖 watcher 时序）
        this.broadcast('reset', null)
        this.$message.success('已挂单，可随时按 F3 取单')
      },
      takeOrder(order) {
        if (this.cart.length) {
          this.$message.warning('当前购物车有商品，请先挂单（F2）或清空')
          return
        }
        this.cart = order.items || []
        this.member = order.member || null
        if (order.depotId) {
          this.depotId = order.depotId
        }
        this.removePending(order)
        this.takeVisible = false
        // 副屏显式同步：取单立即恢复清单与会员态（不依赖 watcher 防抖时序）
        this.broadcastCart()
        if (this.member) {
          this.broadcastMember()
        }
        this.$message.success(`已取回挂单 ${order.no}`)
      },
      removePending(order) {
        this.pendingOrders = this.pendingOrders.filter(o => o.id !== order.id)
        this.savePending()
      },
      // ===== 结算 =====
      openPay() {
        if (!this.depotId) {
          this.$message.warning('请选择仓库')
          return
        }
        if (!this.cart.length) {
          this.$message.warning('购物车为空')
          return
        }
        this.payVisible = true
      },
      onSettle({ payTypes, eraseAmount, invoiceTitle, payMode, authCode, payOrderNo }) {
        this.settling = true
        const body = {
          memberId: this.member ? this.member.memberId : null,
          depotId: this.depotId,
          eraseAmount: eraseAmount || 0,
          payTypes: payTypes,
          invoiceTitle: invoiceTitle || null,
          items: this.cart.map(i => ({
            extendId: i.extendId,
            operNumber: i.operNumber,
            unitPrice: i.unitPrice,
            remark: i.remark
          }))
        }
        // 付款码支付（B扫C）走真实支付接口；扫码收款（C扫B）支付成功后携流水号结算
        let req
        if (payMode === 'bar' && authCode) {
          req = cashierPayBar({ ...body, authCode })
        } else {
          req = cashierSettle({ ...body, payOrderNo: payOrderNo || undefined })
        }
        req.then(res => {
          if (res && res.code === 200) {
            localStorage.setItem(DEPOT_CACHE_KEY, String(this.depotId))
            this.settleResult = res.data || {}
            this.payVisible = false
            this.payQr = null
            this.doneVisible = true
            this.broadcast('paySuccess', {
              amount: (this.settleResult.total != null ? Number(this.settleResult.total) : this.cartTotal) || 0,
              billNo: this.settleResult.billNo
            })
          } else {
            const msg = (res && res.message) || '结算失败'
            // 网关未开通付款码支付方式（WX_BAR/ALI_BAR）：引导改用扫码收款并自动切换
            if (payMode === 'bar' && msg.indexOf('不支持该支付方式') > -1) {
              this.$message.warning('当前支付网关暂不支持付款码收款，请改用扫码收款（顾客扫商家码）')
              if (this.$refs.payModal) {
                this.$refs.payModal.switchToQrcode()
              }
            } else {
              this.$message.warning(msg)
            }
          }
        }).finally(() => {
          this.settling = false
        })
      },
      onPayCancel() {
        this.payVisible = false
        this.payQr = null
      },
      /** 扫码收款（C扫B）预下单：生成收款二维码（PayModal 轮询状态，副屏同步展示） */
      onCreateQrcode({ payTypes, eraseAmount, invoiceTitle, closePayOrderNo }) {
        this.settling = true
        cashierPayQrcode({
          memberId: this.member ? this.member.memberId : null,
          depotId: this.depotId,
          eraseAmount: eraseAmount || 0,
          payTypes: payTypes,
          invoiceTitle: invoiceTitle || null,
          closePayOrderNo: closePayOrderNo || undefined,
          items: this.cart.map(i => ({
            extendId: i.extendId,
            operNumber: i.operNumber,
            unitPrice: i.unitPrice,
            remark: i.remark
          }))
        }).then(res => {
          if (res && res.code === 200) {
            this.payQr = res.data || null
            if (this.payQr && this.payQr.qrBase64) {
              this.broadcastPayQrcode('data:image/png;base64,' + this.payQr.qrBase64)
            }
          } else {
            this.payQr = null
            this.$message.warning((res && res.message) || '收款码生成失败')
          }
        }).finally(() => {
          this.settling = false
        })
      },
      onNextOrder() {
        this.doneVisible = false
        this.cart = []
        this.settleResult = {}
        this.broadcast('reset', null)
        if (this.$refs.searchBox) {
          this.$refs.searchBox.focus()
        }
      },
      // ===== 顾客副屏（BroadcastChannel 广播） =====
      /** 打开副屏：窗口名固定复用（已开则不再重复开窗），URL 携带租户供免登录广告接口 */
      openCustomerScreen() {
        const tid = (this.userInfo && this.userInfo.tenantId) || 0
        window.open(`/cashier/customer-screen?tenantId=${tid}`, SCREEN_WINDOW_NAME)
      },
      broadcast(type, payload) {
        if (this.screenChannel) {
          this.screenChannel.postMessage({ type, payload })
        }
      },
      /** 购物车快照：行项目品名/数量/单价/小计 + 合计件数金额 */
      broadcastCart() {
        const items = this.cart.map(i => ({
          key: i.key,
          name: i.name,
          operNumber: Number(i.operNumber || 0),
          unitPrice: Number(i.unitPrice || 0),
          subtotal: Number(i.unitPrice || 0) * Number(i.operNumber || 0)
        }))
        this.broadcast('cart', {
          items,
          totalCount: items.reduce((sum, i) => sum + i.operNumber, 0),
          totalAmount: items.reduce((sum, i) => sum + i.subtotal, 0)
        })
      },
      /** 会员积分（当前积分 + 本次可得），防抖跟随购物车变动 */
      broadcastMember() {
        if (!this.member) {
          return
        }
        cashierMemberPoints(this.member.memberId, this.cartTotal).then(res => {
          if (res && res.code === 200) {
            this.broadcast('member', res.data)
          } else {
            this.broadcastMemberFallback()
          }
        }).catch(() => {
          this.broadcastMemberFallback()
        })
      },
      broadcastMemberFallback() {
        if (!this.member) {
          return
        }
        // 积分查询失败兜底：至少展示会员名，积分数位显示 --
        this.broadcast('member', {
          memberName: this.member.contacts || this.member.cardNo,
          currentPoints: null,
          obtainablePoints: null
        })
      },
      scheduleMemberPoints() {
        if (this.pointsTimer) {
          clearTimeout(this.pointsTimer)
        }
        this.pointsTimer = setTimeout(() => {
          this.pointsTimer = null
          if (this.member) {
            this.broadcastMember()
          }
        }, MEMBER_POINTS_DEBOUNCE)
      },
      /** 支付二维码（扫码收款预下单成功后调用）：广播二维码图（data URI）到副屏支付态 */
      broadcastPayQrcode(url) {
        this.broadcast('payQrcode', { url })
      },
      // ===== 快捷键（F1锁屏 F2挂单 F3取单 F4退货 F5搜索 F9结算 F12交班） =====
      handleKey(e) {
        if (this.locked) {
          return
        }
        if (this.payVisible || this.doneVisible || this.specVisible || this.returnVisible) {
          return
        }
        switch (e.key) {
          case 'F1':
            e.preventDefault()
            this.locked = true
            break
          case 'F2':
            e.preventDefault()
            this.hangOrder()
            break
          case 'F3':
            e.preventDefault()
            if (this.takeVisible) {
              this.takeVisible = false
            } else {
              this.loadPending()
              this.takeVisible = true
            }
            break
          case 'F4':
            e.preventDefault()
            this.returnVisible = true
            break
          case 'F5':
            e.preventDefault()
            if (this.$refs.searchBox) {
              this.$refs.searchBox.focus()
            }
            break
          case 'F9':
            e.preventDefault()
            this.openPay()
            break
          case 'F12':
            e.preventDefault()
            this.shiftVisible = !this.shiftVisible
            break
          default:
            break
        }
      }
    }
  }
</script>

<style scoped>
  .cashier-desk {
    height: calc(100vh - 130px);
    display: flex;
    flex-direction: column;
    background: #f5f5f5;
    padding: 10px;
    gap: 10px;
    overflow: hidden;
  }
  /* /pos 独立全屏窗口（无 TabLayout 头部） */
  .cashier-desk.in-pos {
    height: 100vh;
  }
  .desk-main {
    flex: 1;
    display: flex;
    gap: 10px;
    min-height: 0;
  }
  .desk-left {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .goods-area {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-radius: 6px;
    border: 1px solid #eee;
    padding: 8px;
  }
  .goods-area .goods-grid {
    flex: 1;
  }
  .goods-more {
    text-align: center;
    padding-top: 6px;
    flex-shrink: 0;
  }
  .goods-end {
    color: #bbb;
    font-size: 12px;
  }
  .desk-right {
    width: 360px;
    flex-shrink: 0;
    min-height: 0;
  }
  .desk-depot {
    flex-shrink: 0;
    color: #555;
    font-size: 13px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
  .pending-empty {
    text-align: center;
    color: #bbb;
    padding: 40px 0;
  }
  .pending-card {
    border: 1px solid #eee;
    border-radius: 6px;
    padding: 10px 14px;
    margin-bottom: 8px;
  }
  .pending-line {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .pending-line.sub {
    color: #999;
    font-size: 12px;
    margin-top: 4px;
    justify-content: space-between;
  }
  .pending-no {
    font-weight: 600;
    color: #333;
  }
  .pending-member {
    flex: 1;
    color: #555;
  }
  .pending-amount {
    color: #e4393c;
    font-weight: 600;
  }
</style>
